/**
 * Batch de activación/desactivación masiva de cuentas de un collector.
 *
 * Es API de webadmin2 → JWT post-MFA (`webadmin2.jwt`), como collectorStats.ts /
 * debines.ts (NO el corejwt de collectors.ts). El trabajo pesado (miles de altas/
 * bajas reales en el banco) corre server-side y desacoplado del browser; acá solo
 * se dispara y se pollea el estado para mostrar progreso y bloquear los botones.
 */
const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "http://localhost:3000";

export type BatchAction = "disable" | "enable";

export interface BatchFailure {
  cvu:    string;
  reason: string;
}

export interface BatchStatus {
  collector_id:   string;
  status:         "idle" | "running" | "done" | "error";
  action:         BatchAction | null;
  processed:      number;
  ok:             number;
  failed:         number;
  total:          number | null;
  started_at:     string | null;
  finished_at:    string | null;
  started_by:     string | null;
  failures:       BatchFailure[];
  disabled_count: number;   // cuántas cuentas hay registradas como "bajadas por admin"
  running:        boolean;
}

function authHeader(): Record<string, string> {
  const jwt = localStorage.getItem("webadmin2.jwt");
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
}

async function authFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const res = await fetch(url, init);
  if (res.status === 401) {
    localStorage.removeItem("webadmin2.jwt");
    localStorage.removeItem("webadmin2.email");
    if (typeof window !== "undefined") window.location.replace("/login");
  }
  return res;
}

export async function getBatchStatus(collectorId: string): Promise<BatchStatus> {
  const res = await authFetch(`${API_BASE}/collectors/${encodeURIComponent(collectorId)}/batch`, { headers: authHeader() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? `getBatchStatus: ${res.status}`);
  return data as BatchStatus;
}

/** Dispara el batch (async). Devuelve el estado inicial (status=running) para pollear. */
export async function startBatch(collectorId: string, action: BatchAction): Promise<{ triggered: boolean; action: BatchAction; status: BatchStatus }> {
  const res = await authFetch(`${API_BASE}/collectors/${encodeURIComponent(collectorId)}/batch`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({ action }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? `startBatch: ${res.status}`);
  return data;
}
